import { defineStore } from 'pinia'

import {
    getModifiers,
    updateModifiers,
    deleteModifiers,
    deleteAllModifiers,
} from '@/api/call-tracking/campaign/campaign-rtb-modifier-routes'

import { checkEqualsArrays, copyArray, getRandomUUID } from '@/helpers/app-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailCampaignStore } from '@/store/call-tracking/campaigns/detailCampaignStore'
import { useDetailCampaignRtbParametersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbParametersStore'
import { useDetailCampaignRtbStore } from '@/store/call-tracking/campaigns/detailCampaignRtbStore'

export const useDetailCampaignRtbModifiersStore = defineStore('detailCampaignRtbModifiers', {
    state: () => ({
        clearLoading: false,
        updateLoading: false,

        items: [],
        copyItems: [],
        removeItems: [],

        enabledSave: true,
        enableValidate: false,
        showErrorInDialog: false,
        errorMaxLengths: 0,
    }),

    getters: {
        campaignSlug() {
            const detailCampaignStore = useDetailCampaignStore()
            return detailCampaignStore.slug
        },

        itemsWithoutRemove(state) {
            return state.items.filter((item) => !state.removeItems.some((removedItem) => {
                const matchById = removedItem.id && removedItem.id === item.id
                const matchByNewId = removedItem.newId && removedItem.newId === item.newId
                return matchById || matchByNewId
            }))
        },

        noModify(state) {
            return checkEqualsArrays(this.itemsWithoutRemove, state.copyItems)
        },

        activateOverrideFirstTime() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.activeOverrideFirstTime
        },

        numberId() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.formData.number_id
        },

        numberFormData() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.formData
        },

        vendorId() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.formData.vendor_id
        },
    },

    actions: {
        checkValueForErrorMaxLength() {
            this.resetErrorMaxLengths()

            this.itemsWithoutRemove.forEach((item) => {
                if (item.values && Array.isArray(item.values)) {
                    item.values.forEach((value) => {
                        if (value.parameter_value && value.parameter_value.length > 255) {
                            this.errorMaxLengths += 1
                        }
                    })
                }
            })
        },

        resetErrorMaxLengths() {
            this.errorMaxLengths = 0
        },

        changeEnabledSave(enabledSave) {
            this.enabledSave = enabledSave
        },

        changeValidate(enableValidate) {
            this.enableValidate = enableValidate
        },

        changeValidateBySave() {
            this.changeValidate(true)

            setTimeout(() => {
                this.changeValidate(false)
            }, 0)
        },

        changeErrorInDialog(showErrorInDialog) {
            this.showErrorInDialog = showErrorInDialog
        },

        updateModifiersAfterRemoveParameter(id) {
            const items = [...this.items].filter((item) => item.parameter_id !== id)
            this.clearModifier()
            this.parsePayloadModifier(items)
        },

        removeModifier(removeItem) {
            const { newId } = removeItem

            if (newId) {
                this.items = this.items.filter((item) => item.newId !== newId)
            }

            this.removeItems.push(removeItem)
        },

        updateModifier(newModifier) {
            const findIndex = this.items.findIndex((item) => (item.id && item.id === newModifier.id) || (item.newId && item.newId === newModifier.newId))

            if (findIndex !== -1) {
                this.items.splice(findIndex, 1, newModifier)
            }
        },

        updateOrderModifier(newModifiers) {
            this.items = newModifiers
        },

        addModifier() {
            const newModifier = {
                required: 0,
                order: this.items.length + 1,
                parameter_id: null,
                operator_name: null,
                operator_key: null,
                file_name: null,
                values: [
                    {
                        parameter_value: null,
                        operation: null,
                        amount: null,
                    },
                ],
                newId: getRandomUUID(),
            }

            const newItems = [...this.items, newModifier]

            if (this.items.length) {
                this.items = newItems
            } else {
                this.setItems(newItems)
            }
        },

        returnToPreviousChanges() {
            this.items = copyArray(this.copyItems)
            this.clearRemoveItems()
        },

        clearModifier() {
            this.items = []
            this.copyItems = []
        },

        clearRemoveItems() {
            this.removeItems = []
        },

        setItems(payload) {
            this.items = payload
            this.copyItems = copyArray(payload)
        },

        findDuplicate(isDuplicate) {
            if (!isDuplicate) {
                return false
            }

            const detailCampaignRtbParametersStore = useDetailCampaignRtbParametersStore()
            return detailCampaignRtbParametersStore.findDuplicate(this.itemsWithoutRemove)
        },

        parsePayloadModifier(payload) {
            if (payload.length) {
                this.setItems(payload)
            } else {
                this.addModifier()
                this.setItems(this.items)
            }
        },

        async goToUpdateModifiers(saveAsNew = false, hideMessage = false) {
            const defaultStore = useDefaultStore()

            if (!this.itemsWithoutRemove.length) {
                this.clearRemoveItems()
                return { success: true }
            }

            this.updateLoading = true

            const conditions = this.itemsWithoutRemove.map((item, index) => ({
                ...item,
                id: saveAsNew ? null : item.id,
                newId: saveAsNew ? getRandomUUID() : null,
                order: index + 1,
            }))

            const { success, payload, message } = await updateModifiers({
                slug: this.campaignSlug,
                formData: {
                    number_id: this.numberId,
                    vendor_id: this.vendorId,
                    conditions,
                },
            })

            if (success) {
                this.clearRemoveItems()
                this.parsePayloadModifier(payload)

                if (!hideMessage) {
                    defaultStore.setSuccessMessage({ message })
                }
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success }
        },

        async goToDeleteModifiers() {
            const items = this.removeItems.filter((item) => !item.newId)

            if (!items.length) {
                return { success: true }
            }

            this.updateLoading = true

            const { success, message } = await deleteModifiers({
                slug: this.campaignSlug,
                data: {
                    number_id: this.numberId,
                    ids: items.map((item) => item.id),
                },
            })

            if (!success) {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success, message }
        },

        async goToDeleteAllModifiers() {
            this.clearLoading = true

            const defaultStore = useDefaultStore()

            const { success, message } = await deleteAllModifiers({
                slug: this.campaignSlug,
                data: {
                    number_id: this.numberId,
                },
            })

            if (success) {
                this.clearRemoveItems()
                this.clearModifier()
                this.parsePayloadModifier([])
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.clearLoading = false

            return { success }
        },

        async goToLoadModifiers(isDuplicate = false) {
            const defaultStore = useDefaultStore()

            const isDuplicateFound = this.findDuplicate(isDuplicate)

            const { success, payload, message } = await getModifiers({
                slug: this.campaignSlug,
                params: {
                    number_id: this.activateOverrideFirstTime ? null : this.numberId,
                },
            })

            if (success) {
                this.setItems([])
                this.parsePayloadModifier(payload)

                if (isDuplicateFound) {
                    defaultStore.setErrorMessage({ message: 'Any condition with the “Is duplicate” operator has been deleted' })
                }
            } else {
                defaultStore.setErrorMessage({ message })
            }

            return { success, payload }
        },
    },
})
